import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { Belt } from '../belt';

/**
 * The section's glyph, from the hand-off. Declared next to its only consumer, the way the footer
 * declares its LinkedIn badge — `icons.tsx` is the language selector's flags.
 */
function CoinArrowOut(props: { className?: string }) {
	return (
		<svg
			viewBox="0 0 32 32"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			{...props}
		>
			<path d="M12.6667 18.3334C12.6667 19.6267 13.6668 20.6667 14.8934 20.6667H17.4001C18.4667 20.6667 19.3334 19.7601 19.3334 18.6267C19.3334 17.4134 18.8001 16.9734 18.0134 16.6934L14.0001 15.2934C13.2134 15.0134 12.6801 14.5867 12.6801 13.3601C12.6801 12.2401 13.5467 11.3201 14.6134 11.3201H17.1201C18.3467 11.3201 19.3468 12.3601 19.3468 13.6534" />
			<path d="M16 10V22" />
			<path d="M29.3334 16.0001C29.3334 23.3601 23.3601 29.3334 16.0001 29.3334C8.64008 29.3334 2.66675 23.3601 2.66675 16.0001C2.66675 8.64008 8.64008 2.66675 16.0001 2.66675" />
			<path d="M29.3333 8.00008V2.66675H24" />
			<path d="M22.6667 9.33341L29.3334 2.66675" />
		</svg>
	);
}

/**
 * Payment gateways and technology partners we integrate with. The files are shared with `www`, which
 * keeps a list of its own — a logo added or dropped here has to be done there too, or the two pages
 * disagree about who we work with.
 *
 * The older files carry an opaque white plate, dropped at render time with `mix-blend-multiply` rather
 * than by editing them — the CDN derivative URL is keyed on the source path, not its contents, so an
 * edit would keep serving the old crop until someone purged it. Multiply leaves a transparent file
 * alone, so the newer ones ride along untouched. THIS DEPENDS ON THE TILE STAYING LIGHT; on a dark one
 * multiply would swallow the logos whole.
 *
 * `file` carries its EXTENSION. Most are 125x55 legacy rasters, which is why their fine print reads
 * soft; the newer artwork is larger, and Paycomet is the vector the rest of them want to be.
 */
const TECH_PARTNERS: { file: string; name: string }[] = [
	{ file: 'sabadell.png', name: 'Banco Sabadell' },
	{ file: 'paytef.png', name: 'PayTEF' },
	{ file: 'mastercard.png', name: 'Mastercard' },
	{ file: 'adyen.png', name: 'Adyen' },
	{ file: 'comercia-global-payments.png', name: 'Comercia Global Payments' },
	{ file: 'paycomet.svg', name: 'Paycomet' },
	{ file: 'wordline.png', name: 'Worldline' },
	{ file: 'infinitypay.png', name: 'InfinityPay' },
	{ file: 'necomplus.png', name: 'Necomplus' },
	{ file: 'sipay.png', name: 'Sipay' },
	{ file: 'toshiba.png', name: 'Toshiba' },
	{ file: 'diebold-nixford.png', name: 'Diebold Nixdorf' },
	{ file: 'marketpay.png', name: 'MarketPay' },
	{ file: 'ecopaynet.png', name: 'Ecopaynet' },
	{ file: 'saima-systems.png', name: 'Saima Systems' },
	{ file: 'sistemas-avanzados-en-monetica.png', name: 'Sistemas Avanzados en Monética' },
];

// Tile geometry, applied as inline styles because it drives the layout and has to stay in step with
// the gap the belt loops on: a Tailwind class that drifted from `TILE_GAP` would show a seam.
const TILE_W = 190;
const TILE_H = 96;
const TILE_GAP = 20;
/**
 * Sized to the pixels that exist: the files are 125x55, so 55 is the tallest a mark renders without the
 * browser inventing detail. imgproxy never enlarges (`generateImagePath` passes no `enlarge`), so
 * asking for more just stretches the same bitmap.
 */
const LOGO_BOX_W = TILE_W - 24;
const LOGO_H = 55;
const COLUMN_H = 560;
/** px per second, the speed the belts drift at. */
const SPEED = 26;

/** One tile, shared by the desktop columns and the mobile rows — only the box around it changes. */
function LogoTile(props: { partner: (typeof TECH_PARTNERS)[number]; style: React.CSSProperties; boxW: number }) {
	return (
		<div style={props.style} className="bg-ink/5 isolate flex shrink-0 items-center justify-center rounded-lg px-3">
			{/* The logo box is sized HERE, in the same pixels the CDN request asks for, instead of being left
			    to the image's own width. `Image` renders a `<picture>`, and that — not the `<img>` — is the
			    flex item: its width is shrink-to-fit, and a percentage width on the child does not contribute
			    to it, so the box ended up as wide as the LOADED bitmap. imgproxy never enlarges, and all but
			    two of these files are 125px wide, so the box came out at 125 and `object-contain` then fitted
			    the mark inside a box shorter than 55px as well — the logo drew about a third small, and only
			    once the file had landed, which is why it came and went. `display:contents` on the picture
			    hands this box straight to the `<img>`. */}
			<div style={{ width: props.boxW, height: LOGO_H }} className="[&>picture]:contents">
				{props.partner.file.endsWith('.svg') ? (
					/* A vector goes straight to the browser: imgproxy exists to produce a raster derivative,
					   and rasterizing artwork that already scales is the one thing it should not be asked
					   for. `eager` for the same reason `Image` takes the flag — a lazy logo on a marquee
					   only starts loading once it has slid into view, and pops in mid-motion. */
					<img
						src={`/home/tech-partners/${props.partner.file}`}
						alt={props.partner.name}
						loading="eager"
						fetchPriority="low"
						className="size-full object-contain mix-blend-multiply"
					/>
				) : (
					<Image
						src={`/home/tech-partners/${props.partner.file}`}
						alt={props.partner.name}
						height={LOGO_H}
						aspectRatio={props.boxW / LOGO_H}
						objectFit="contain"
						className="size-full object-contain! mix-blend-multiply"
					/>
				)}
			</div>
		</div>
	);
}

/** A column of logos on an endless belt. The per-tile bottom margin is what the loop needs. */
function LogoColumn(props: { logos: typeof TECH_PARTNERS; reverse?: boolean }) {
	return (
		<div
			style={{ height: COLUMN_H, width: TILE_W }}
			className="overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]"
		>
			<Belt axis="y" speed={SPEED} reverse={props.reverse}>
				{props.logos.map((partner, i) => (
					<LogoTile
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						partner={partner}
						boxW={LOGO_BOX_W}
						style={{ height: TILE_H, marginBottom: TILE_GAP }}
					/>
				))}
			</Belt>
		</div>
	);
}

// Mobile row geometry. px per second, matching the merchant wheel's mobile rows so both logo walls on
// the page read as one motion.
const MOBILE_SPEED = 32;
const MOBILE_TILE_W = 150;
const MOBILE_TILE_H = 76;
const MOBILE_GAP = 12;

/** Below `xl` the belts become horizontal marquees; a column could only show one of the two. */
function LogoRowMobile(props: { logos: typeof TECH_PARTNERS; reverse?: boolean }) {
	return (
		<div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
			<Belt speed={MOBILE_SPEED} reverse={props.reverse}>
				{props.logos.map((partner, i) => (
					<LogoTile
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						partner={partner}
						boxW={MOBILE_TILE_W - 24}
						style={{ width: MOBILE_TILE_W, height: MOBILE_TILE_H, marginRight: MOBILE_GAP }}
					/>
				))}
			</Belt>
		</div>
	);
}

/** "Cómo lo hacemos posible" — two belts drifting in opposite directions, faded out top and bottom. */
export function TechPartners() {
	const { t } = useLingui();
	const half = Math.ceil(TECH_PARTNERS.length / 2);

	return (
		<section id="tech-partners" className="scroll-mt-24 overflow-hidden bg-white">
			<div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 py-16 md:py-20 lg:grid-cols-[minmax(0,1fr)_auto] xl:px-10">
				<div className="flex flex-col items-start gap-6">
					<div className="relative">
						{/* Tilted orange plate peeking from behind the icon, as in the hand-off. */}
						<div
							aria-hidden
							className="bg-brand absolute inset-0 translate-x-1 translate-y-1 rotate-[10deg] rounded-lg"
						/>
						<div className="bg-ink relative grid size-16 place-items-center rounded-lg text-white">
							<CoinArrowOut className="size-8" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How we make it possible`}
						</h2>
						<p className="text-ink max-w-[533px] text-base leading-[1.5]">
							{t`We hold partnership agreements with the main payment gateways on the market and with technology partners, so the integration is native and secure.`}
						</p>
					</div>
				</div>

				<div className="hidden justify-center gap-5 xl:flex">
					<LogoColumn logos={TECH_PARTNERS.slice(0, half)} />
					<LogoColumn logos={TECH_PARTNERS.slice(half)} reverse />
				</div>

				{/* Split by index rather than in half, so neither row ends up holding all the wide wordmarks. */}
				<div className="flex flex-col gap-3 xl:hidden">
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 0)} />
					<LogoRowMobile logos={TECH_PARTNERS.filter((_, i) => i % 2 === 1)} reverse />
				</div>
			</div>
		</section>
	);
}
